import { createClient, SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './database.types';
import type { StorageAdapter } from './storage';

export type TypedSupabaseClient = SupabaseClient<Database>;

export interface CreateSupabaseClientOptions {
  url: string;
  publishableKey: string;
  storage: StorageAdapter;
  persistSession?: boolean;
  autoRefreshToken?: boolean;
  realtimeTimeout?: number;
  heartbeatIntervalMs?: number;
}

export function createSupabaseClient(options: CreateSupabaseClientOptions): TypedSupabaseClient {
  const {
    url,
    publishableKey,
    storage,
    persistSession = true,
    autoRefreshToken = true,
    realtimeTimeout = 20000,
    heartbeatIntervalMs = 15000,
  } = options;

  return createClient<Database>(url, publishableKey, {
    auth: {
      storage: {
        getItem: (key) => storage.getItem(key),
        setItem: (key, value) => storage.setItem(key, value),
        removeItem: (key) => storage.removeItem(key),
      },
      persistSession,
      autoRefreshToken,
    },
    realtime: {
      timeout: realtimeTimeout,
      heartbeatIntervalMs,
    },
  });
}

/**
 * Re-export for convenience when a global client is initialized by the app layer.
 */
export let supabase: TypedSupabaseClient | null = null;

export function initializeSupabaseClient(options: CreateSupabaseClientOptions) {
  supabase = createSupabaseClient(options);
  return supabase;
}

export type { Database } from './database.types';
